import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';
import { performCompleteLogout } from '../../utils/logout';

export interface User {
  id: string;
  email: string;
  name: string;
  companyName?: string;
  role: string;
  avatar?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  verified_domain:boolean;
  is_suspended: boolean;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: false,
  error: null,
  verified_domain: localStorage.getItem('verified_domain') === 'true',
  is_suspended: localStorage.getItem('is_suspended') === 'true',
};

// Async thunks for API calls
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/auth/login', credentials);

      localStorage.setItem('token', response.token);
       localStorage.setItem('email', response.data.email);
       localStorage.setItem('full_name', response.data.full_name);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/auth/register', userData);
      localStorage.setItem('token', response.token);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/auth/forgot-password', { email });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send reset email');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data: { token: string; password: string }, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/auth/reset-password', data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Password reset failed');
    }
  }
);

export const verifyAuthCode = createAsyncThunk(
  'auth/verifyCode',
  async (code: string, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/auth/verify-code', { code });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Code verification failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await apiService.get('/auth/me');
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get user');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await performCompleteLogout(false); // Don't show toast in Redux action
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      // This is just for state cleanup, actual logout is handled by logoutUser thunk
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.verified_domain = false;
      state.is_suspended = false;
      localStorage.removeItem('verified_domain');
      localStorage.removeItem('is_suspended');
    },
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
    },
    setDomainVerified: (state, action: PayloadAction<boolean>) => {
        state.verified_domain = action.payload;
        localStorage.setItem('verified_domain', action.payload ? 'true' : 'false');
    },
    setSuspended: (state, action: PayloadAction<boolean>) => {
        state.is_suspended = action.payload;
        localStorage.setItem('is_suspended', action.payload ? 'true' : 'false');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        if (action.payload?.data?.domain_verified !== undefined && action.payload?.data?.domain_verified !== null) {
          state.verified_domain = !!action.payload.data.domain_verified;
          localStorage.setItem('verified_domain', action.payload.data.domain_verified ? 'true' : 'false');
        }
        if (action.payload?.data?.is_suspended !== undefined && action.payload?.data?.is_suspended !== null) {
          state.is_suspended = !!action.payload.data.is_suspended;
          localStorage.setItem('is_suspended', action.payload.data.is_suspended ? 'true' : 'false');
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        if (action.payload?.data?.domain_verified !== undefined && action.payload?.data?.domain_verified !== null) {
          state.verified_domain = !!action.payload.data.domain_verified;
          localStorage.setItem('verified_domain', action.payload.data.domain_verified ? 'true' : 'false');
        }
        if (action.payload?.data?.is_suspended !== undefined && action.payload?.data?.is_suspended !== null) {
          state.is_suspended = !!action.payload.data.is_suspended;
          localStorage.setItem('is_suspended', action.payload.data.is_suspended ? 'true' : 'false');
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Verify Code
      .addCase(verifyAuthCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyAuthCode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(verifyAuthCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get Current User
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload.user || action.payload;
        state.isAuthenticated = true;
        if (action.payload?.data?.is_suspended !== undefined && action.payload?.data?.is_suspended !== null) {
          state.is_suspended = !!action.payload.data.is_suspended;
          localStorage.setItem('is_suspended', action.payload.data.is_suspended ? 'true' : 'false');
        }
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        localStorage.removeItem('token');
      })
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
        state.verified_domain = false;
        state.is_suspended = false;
        localStorage.removeItem('verified_domain');
        localStorage.removeItem('is_suspended');
      })
      .addCase(logoutUser.rejected, (state, action) => {
        // Even if logout fails, clear the state
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = action.payload as string;
        state.isLoading = false;
        state.verified_domain = false;
        state.is_suspended = false;
        localStorage.removeItem('verified_domain');
        localStorage.removeItem('is_suspended');
      });
  },
});

export const { logout, clearError, setCredentials, setDomainVerified, setSuspended } = authSlice.actions;
export default authSlice.reducer;